import React from "react";
import { useTranslation } from "react-i18next";
import { Facebook, Instagram, Heart } from "lucide-react";
import { LLink } from "@/i18n/LLink";

const ONTDEK_LEMMER = [
  { key: "home", tKey: "home", to: "/" },
  { key: "eten-drinken", tKey: "eten_drinken", to: "/eten-en-drinken" },
  { key: "winkelen", tKey: "winkelen", to: "/winkelen" },
  { key: "zien-doen", tKey: "zien_doen", to: "/zien-en-doen" },
  { key: "watersport-haven", tKey: "watersport_haven", to: "/watersport-haven" },
];

const PRAKTISCH_LINKS = [
  { key: "overnachten", tKey: "overnachten", to: "/overnachten" },
  { key: "agenda", tKey: "agenda", to: "/agenda" },
  { key: "webcams", tKey: "webcams", to: "/webcams" },
  { key: "praktisch", tKey: "praktisch", to: "/praktisch" },
  { key: "bedrijvengids", tKey: "bedrijvengids", to: "/bedrijvengids" },
  { key: "over-ons", tKey: "over_ons", to: "/over-ons" },
  { key: "contact", tKey: "contact", to: "/contact" },
];

export const Footer = () => {
  const { t } = useTranslation();
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#F4F8FB] text-[#3E5871] mt-16 border-t border-[#E4EEF5]"
    >
      <svg
        className="w-full block"
        viewBox="0 0 1440 60"
        preserveAspectRatio="none"
        style={{ height: 40 }}
        aria-hidden="true"
      >
        <path
          d="M0,30 C240,60 480,0 720,20 C960,40 1200,50 1440,20 L1440,0 L0,0 Z"
          fill="#FFFFFF"
        />
      </svg>

      <div className="ll-container py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Intro */}
          <div className="lg:col-span-1">
            <LLink to="/" className="inline-block mb-4">
              <img
                src="/logos/footer-lemmer-leeft.png"
                alt="Lemmer Leeft"
                className="h-24 sm:h-28 w-auto"
              />
            </LLink>
            <p className="text-sm text-[#3E5871] leading-relaxed">
              {t("footer.intro")}
            </p>
          </div>

          {/* Nav kolom 1 */}
          <div>
            <h4 className="text-[#173A5E] text-sm font-[Poppins] font-semibold uppercase tracking-wider mb-4">
              {t("footer.ontdek_lemmer")}
            </h4>
            <ul className="space-y-2">
              {ONTDEK_LEMMER.map((item) => (
                <li key={item.to}>
                  <LLink
                    to={item.to}
                    data-testid={`footer-link-${item.key}`}
                    className="text-sm text-[#3E5871] hover:text-[#2E9CD6] transition"
                  >
                    {t(`nav.${item.tKey}`)}
                  </LLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Nav kolom 2 */}
          <div>
            <h4 className="text-[#173A5E] text-sm font-[Poppins] font-semibold uppercase tracking-wider mb-4">
              {t("footer.praktisch")}
            </h4>
            <ul className="space-y-2">
              {PRAKTISCH_LINKS.map((item) => (
                <li key={item.to}>
                  <LLink
                    to={item.to}
                    data-testid={`footer-link-${item.key}`}
                    className="text-sm text-[#3E5871] hover:text-[#2E9CD6] transition"
                  >
                    {t(`nav.${item.tKey}`)}
                  </LLink>
                </li>
              ))}
              <li>
                <LLink
                  to="/voor-ondernemers"
                  data-testid="footer-link-ondernemers"
                  className="text-sm text-[#173A5E] hover:text-[#2E9CD6] font-medium transition"
                >
                  {t("nav.voor_ondernemers")}
                </LLink>
              </li>
            </ul>
          </div>

          {/* Social + initiatief */}
          <div>
            <h4 className="text-[#173A5E] text-sm font-[Poppins] font-semibold uppercase tracking-wider mb-4">
              {t("footer.volg_lemmer")}
            </h4>
            <div className="flex items-center gap-3 mb-6">
              <a
                href="https://www.facebook.com/"
                target="_blank"
                rel="noreferrer"
                data-testid="social-facebook"
                aria-label="Facebook"
                className="w-10 h-10 rounded-full bg-white hover:bg-[#2E9CD6] flex items-center justify-center transition border border-[#E4EEF5] text-[#173A5E] hover:text-white"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/"
                target="_blank"
                rel="noreferrer"
                data-testid="social-instagram"
                aria-label="Instagram"
                className="w-10 h-10 rounded-full bg-white hover:bg-[#2E9CD6] flex items-center justify-center transition border border-[#E4EEF5] text-[#173A5E] hover:text-white"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
            <p className="text-xs text-[#5A7594] leading-relaxed">
              {t("footer.initiatief")}
              <br />
              <span className="text-[#173A5E] font-semibold text-sm">
                BIZ Vereniging Lemmer
              </span>
            </p>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-[#E4EEF5] flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#5A7594]">
          <p>{t("footer.copyright", { year: new Date().getFullYear() })}</p>
          <div className="flex items-center gap-4">
            <LLink
              to="/colofon"
              data-testid="footer-link-colofon"
              className="text-[#5A7594] hover:text-[#2E9CD6] transition"
            >
              {t("footer.colofon")}
            </LLink>
            <a
              href="https://ghio.nl"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-link-ghio"
              className="inline-flex items-center gap-1 text-[#5A7594] hover:text-[#2E9CD6] transition"
            >
              {t("footer.gemaakt_met")}
              <Heart
                className="w-3.5 h-3.5 fill-[#CE1B2A] text-[#CE1B2A]"
                aria-label="♥"
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
